/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import type { PlatformContractInfo, PlatformUpdateContractInfoRequest } from 'account-client'
import { Col, Form, Input, message, Modal, Row, Select } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { accountClient } from '@/services/cloudClient/accountClient'

const { Option } = Select

const buyerTypeOptions = [
    { value: 'E', label: 'Enterprise' },
    { value: 'P', label: 'Individual' },
]

// 辅助函数：将空字符串转换为undefined
const processContractData = (data: PlatformContractInfo): PlatformContractInfo => {
    return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
            key,
            value === '' ? undefined : value
        ])
    ) as PlatformContractInfo
}

interface KycEditModalProps {
    visible: boolean
    contractId: string
    onCancel: () => void
    onSuccess: () => void
}

export function KycEditModal({ visible, contractId, onCancel, onSuccess }: KycEditModalProps) {
    const { t } = useTranslation()
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [kycInfo, setKycInfo] = useState<PlatformContractInfo | null>(null)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (visible && contractId) {
            setLoading(true)
            accountClient.contractsApi.getContractInfo(contractId)
                .then((res: { data: PlatformContractInfo }) => {
                    const processedData = processContractData(res.data)
                    setKycInfo(processedData)
                    form.setFieldsValue(processedData)
                })
                .catch((error) => {
                    console.error('Failed to get KYC information:', error)
                    message.error(t('Failed to get KYC information'))
                })
                .finally(() => setLoading(false))
        }
    }, [visible, contractId, form, t])

    const handleSave = async () => {
        try {
            const values = await form.validateFields()
            setSaving(true)
            const req: PlatformUpdateContractInfoRequest = {
                ...values,
                buyerType: kycInfo?.buyerType || values.buyerType,
            }
            await accountClient.contractsApi.updateContractInfo(contractId, req)
            message.success(t('KYC information updated successfully'))
            onSuccess()
            onCancel()
        } catch (err: any) {
            if (err?.errorFields) return // 表单校验错误
            console.error('Save failed:', err)
            message.error(t('Save failed, please check input or try again later'))
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        form.resetFields()
        onCancel()
    }

    return (
        <Modal
            title={t('Edit KYC Information')}
            open={visible}
            onCancel={handleCancel}
            width={800}
            confirmLoading={saving}
            onOk={handleSave}
            okText={t('Save')}
            cancelText={t('Cancel')}
        >
            <Form
                form={form}
                layout="vertical"
                disabled={loading}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="buyerType"
                            label={t('Buyer Type')}
                            rules={[{ required: true, message: t('Please select buyer type') }]}
                        >
                            <Select disabled>
                                {buyerTypeOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="tags"
                            label={t('Contract Tags')}
                        >
                            <Input placeholder={t('Enter contract tags')} />
                        </Form.Item>
                    </Col>
                </Row>

                {kycInfo?.buyerType === 'E' ? (
                    // Enterprise fields
                    <>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="enterpriseName"
                                    label={t('Legal Entity Name')}
                                    rules={[{ required: true, message: t('Please enter legal entity name') }]}
                                >
                                    <Input placeholder={t('Enter legal entity name')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="enterpriseSocialId"
                                    label={t('Social ID')}
                                    rules={[
                                        { required: true, message: t('Please enter social ID') },
                                        // 暂时移除长度校验，因为还不确定PB和TN分别的限制
                                        // { len: 18, message: t('Social ID must be 18 characters') },
                                        { pattern: /^[0-9A-Za-z]+$/, message: t('Only letters and numbers allowed') }
                                    ]}
                                >
                                    <Input placeholder={t('Enter social ID')} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="enterpriseLegalName"
                                    label={t('Legal Representative')}
                                    rules={[{ required: true, message: t('Please enter legal representative name') }]}
                                >
                                    <Input placeholder={t('Enter legal representative name')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="enterpriseLegalSocialId"
                                    label={t('Legal Representative ID')}
                                    rules={[
                                        { required: true, message: t('Please enter legal representative ID') },
                                        // 暂时移除长度校验，因为还不确定PB和TN分别的限制
                                        // { len: 18, message: t('ID must be 18 characters') },
                                        { pattern: /^[0-9A-Za-z]+$/, message: t('Only letters and numbers allowed') }
                                    ]}
                                >
                                    <Input placeholder={t('Enter legal representative ID')} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="contactPerson"
                                    label={t('Contact Person')}
                                    rules={[{ required: true, message: t('Please enter contact person') }]}
                                >
                                    <Input placeholder={t('Enter contact person')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="address"
                                    label={t('Registered Address')}
                                    rules={[{ required: true, message: t('Please enter registered address') }]}
                                >
                                    <Input placeholder={t('Enter registered address')} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                ) : (
                    // Individual fields
                    <>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="citizenName"
                                    label={t('Full Name')}
                                    rules={[{ required: true, message: t('Please enter full name') }]}
                                >
                                    <Input placeholder={t('Enter full name')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="citizenSocialId"
                                    label={t('Identity ID')}
                                    rules={[
                                        { required: true, message: t('Please enter identity ID') },
                                        { len: 18, message: t('Identity ID must be 18 characters') },
                                        { pattern: /^[0-9A-Za-z]+$/, message: t('Only letters and numbers allowed') }
                                    ]}
                                >
                                    <Input placeholder={t('Enter identity ID')} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="address"
                                    label={t('Address')}
                                    rules={[{ required: true, message: t('Please enter address') }]}
                                >
                                    <Input placeholder={t('Enter address')} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="contactPhone"
                            label={t('Contact Phone')}
                            rules={[
                                { required: true, message: t('Please enter contact phone') },
                                { pattern: /^1[3-9]\d{9}$/, message: t('Please enter valid phone number') }
                            ]}
                        >
                            <Input placeholder={t('Enter contact phone')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="contactEmail"
                            label={t('Contact Email')}
                            rules={[
                                { required: true, message: t('Please enter contact email') },
                                { type: 'email', message: t('Please enter valid email address') }
                            ]}
                        >
                            <Input placeholder={t('Enter contact email')} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
}
